// src/contexts/NotificationContext.jsx
import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import useNotifications from '../hooks/useNotifications'
import { getNotificationIcon, getNotificationColor } from '../services/notificationService'
import { createAppSocket, bindPageLifecycle } from '../services/ws/appSocket'
import { useAuth } from '../hooks/useAuth'

const NotificationContext = createContext(null)

/**
 * Notification Provider Component
 * Provides notification state and real-time WebSocket updates to the entire app
 */
export const NotificationProvider = ({ children }) => {
  const { notifications, unreadCount, loading, error, pagination, fetchNotifications, fetchUnreadCount, markAsRead, markAllAsRead, deleteNotification, refresh, loadNextPage, loadPrevPage, addNotification } = useNotifications({
    // The sign-in effect below fetches (on load and on any later sign-in);
    // the hook fetching too sent every request twice.
    autoFetch: false,
    pollInterval: 60000, // Poll every 60 seconds as backup (skipped while the tab is hidden)
    showToasts: false // We'll handle toasts manually via WebSocket
  })

  const { user } = useAuth() || {}
  const userId = user?.id ?? null
  // One managed socket per signed-in customer (services/ws/ManagedSocket.js):
  // ticket auth, one attempt at a time, jittered backoff, the shared 429
  // cooldown, one session refresh then stop.
  const socketRef = useRef(null)

  /**
   * Handle incoming WebSocket notification
   */
  const handleWebSocketMessage = useCallback(
    event => {
      try {
        const message = JSON.parse(event.data)

        if (message.type === 'notification' && message.data) {
          const notification = message.data

          // Guard against missing fields
          if (!notification.notification_type) return

          // Add to local state
          addNotification(notification, false)

          // Show toast notification
          toast(
            <div className="notification-toast">
              <div className="notification-toast__icon" style={{ color: getNotificationColor(notification.notification_type) }}>
                <i className={`ph ${getNotificationIcon(notification.notification_type)}`} />
              </div>
              <div className="notification-toast__content">
                <strong>{notification.title}</strong>
                <p>{notification.message}</p>
              </div>
            </div>,
            {
              toastId: notification.id,
              type: 'info',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true
            }
          )
        } else if (message.type === 'unread_count') {
          // Update unread count from server
          fetchUnreadCount()
        }
      } catch (err) {
        console.error('Error parsing WebSocket message:', err)
      }
    },
    [addNotification, fetchUnreadCount]
  )

  // The latest message handler, so a re-created callback never reconnects.
  const handlerRef = useRef(handleWebSocketMessage)
  handlerRef.current = handleWebSocketMessage

  const connectWebSocket = useCallback(() => {
    if (socketRef.current) socketRef.current.restart()
  }, [])

  const disconnectWebSocket = useCallback(() => {
    if (socketRef.current) socketRef.current.stop()
  }, [])

  // Connect on sign-in (here or in another tab), close on sign-out. A token
  // refresh keeps the same socket; the network returning or the tab coming
  // back reconnects at once instead of waiting out the backoff.
  useEffect(() => {
    if (!userId) return undefined
    const socket = createAppSocket({
      path: '/ws/notifications/',
      onMessage: raw => handlerRef.current({ data: raw })
    })
    socketRef.current = socket
    socket.start()
    const unbind = bindPageLifecycle(socket)
    refresh()
    return () => {
      unbind()
      socket.stop()
      socketRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const value = {
    // State
    notifications,
    unreadCount,
    loading,
    error,
    pagination,

    // Actions
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh,
    loadNextPage,
    loadPrevPage,

    // WebSocket
    connectWebSocket,
    disconnectWebSocket
  }

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}

/**
 * Hook to access notification context
 * @returns {Object} Notification context value
 */
export const useNotificationContext = () => {
  const context = useContext(NotificationContext)

  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider')
  }

  return context
}

export default NotificationContext
